import React, { useEffect, useState } from "react";
import {
  getRequest,
  postRequest,
} from "../../../../../../services/api/apiService";
import { toast } from "react-toastify";

// Choix du compte publicitaire Reddit — même rôle et même contrat que les
// modals Snapchat/LinkedIn : on liste les comptes accessibles via l'API et on
// enregistre celui retenu sur la connexion active.
const ConnectedPlatformAccountRedditConfigurationModal = ({
  platformName,
  headers,
  onClose,
  handleFetchConnectedPlatforms,
}) => {
  const [redditAdAccountId, setRedditAdAccountId] = useState("");
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAvailableAccounts = async () => {
      try {
        const response = await getRequest("/api/ads/reddit/ad-accounts", { ...headers });
        if (cancelled) return;
        const accounts = response?.data?.accounts || [];
        setAvailableAccounts(accounts);
        // Pré-sélectionne le compte déjà enregistré.
        const savedId = response?.data?.selectedRedditAdAccountId;
        if (savedId) setRedditAdAccountId(String(savedId));
      } catch (err) {
        if (cancelled) return;
        // Le détail vient du serveur (scope refusé, token expiré…) — plus utile
        // qu'un message générique pour comprendre quoi corriger.
        const detail = err?.response?.data?.message;
        setError(detail || "Échec de récupération des comptes publicitaires Reddit.");
      } finally {
        if (!cancelled) setFetching(false);
      }
    };

    fetchAvailableAccounts();
    return () => { cancelled = true; };
  }, [headers]);

  const saveRedditAdsAccount = async () => {
    if (!redditAdAccountId) {
      setError("Veuillez sélectionner un compte publicitaire Reddit.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await postRequest(
        "/api/ads/reddit/save-reddit-ads-account",
        { redditAdAccountId },
        { ...headers }
      );

      toast.success("Compte Reddit Ads mis à jour ✅");
      onClose();
      handleFetchConnectedPlatforms();
    } catch (err) {
      const detail = err?.response?.data?.message;
      setError(detail || "Une erreur est survenue pendant l'enregistrement du compte Reddit.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Compte publicitaire Reddit
          </label>
          <select
            value={redditAdAccountId}
            onChange={(e) => setRedditAdAccountId(e.target.value)}
            disabled={fetching || availableAccounts.length === 0}
            className="mt-1 p-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none z-10 disabled:bg-gray-100"
          >
            <option value="" disabled>
              {fetching ? "Chargement des comptes…" : "Sélectionner un compte"}
            </option>
            {availableAccounts.map((option) => (
              <option key={option?.id} value={option?.id}>
                {option?.name ? `${option.name} (${option.id})` : option?.id}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="text-xs text-gray-500 italic mb-6 mt-2">
        {!fetching && availableAccounts.length === 0
          ? "Aucun compte publicitaire trouvé sur ce compte Reddit. Vérifiez que vous avez bien accès à un compte Reddit Ads."
          : "L'identifiant du compte Reddit Ads utilisé pour vos campagnes."}
      </div>

      {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

      <div className="flex justify-end gap-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
        >
          Annuler
        </button>
        <button
          onClick={saveRedditAdsAccount}
          disabled={loading || fetching}
          className="px-4 py-2 bg-[#FF4500] text-white rounded-lg hover:bg-[#e03d00] disabled:bg-gray-400"
        >
          {loading ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
};

export default ConnectedPlatformAccountRedditConfigurationModal;
